<script>
	import { onMount } from 'svelte';
	import { fetchDocument } from '../lib/api.js';
	import { authState, initAuth } from '../lib/auth.js';
	import { renderJsondToHtml } from '../lib/jsondRenderer.js';

	const params = new URLSearchParams(window.location.search);
	const docRef = params.get('ref');

	let docData = null;
	let loading = true;
	let error = null;
	let fetchTriggered = false;

	$: jsond = docData?.jsond_content
		? (typeof docData.jsond_content === 'string' ? JSON.parse(docData.jsond_content) : docData.jsond_content)
		: null;

	$: renderedHtml = jsond ? renderJsondToHtml(jsond) : '';

	// Wait for Firebase auth to restore session before fetching —
	// calling fetchDocument immediately in onMount races onAuthStateChanged.
	$: if (!$authState.loading && !fetchTriggered && docRef) {
		fetchTriggered = true;
		loadDoc();
	}

	onMount(() => {
		initAuth(); // idempotent — no-op if App.svelte already called it
		if (!docRef) {
			error = 'No document reference provided.';
			loading = false;
		}
	});

	async function loadDoc() {
		loading = true;
		try {
			docData = await fetchDocument(docRef);
			if (docData?.title) document.title = docData.title + ' — ADRASI';
		} catch (e) {
			error = e.message === 'auth_required'
				? 'Sign in to read this document.'
				: e.message === 'not_found'
				? 'Document not found.'
				: 'Could not load document.';
		} finally {
			loading = false;
		}
	}
</script>

<div class="zen-page">
	{#if loading}
		<div class="zen-loading">Loading…</div>
	{:else if error}
		<div class="zen-error">{error}</div>
	{:else if docData}
		<article class="zen-article">
			<h1 class="zen-title">{docData.title}</h1>
			<div class="zen-body jsond-doc">
				{@html renderedHtml}
			</div>
		</article>
	{/if}
</div>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
		background: #F8F6F2;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
		-webkit-font-smoothing: antialiased;
	}

	.zen-page {
		min-height: 100vh;
		background: #F8F6F2;
		display: flex;
		justify-content: center;
		padding: 64px 24px 96px;
	}

	.zen-loading,
	.zen-error {
		margin-top: 120px;
		font-size: 14px;
		color: #9CA3AF;
		text-align: center;
	}

	.zen-article {
		width: 100%;
		max-width: 680px;
	}

	.zen-title {
		font-size: 26px;
		font-weight: 700;
		color: #111827;
		margin: 0 0 36px;
		line-height: 1.25;
		letter-spacing: -0.3px;
	}

	/* ---- JSOND body resets for zen context ---- */

	.zen-body :global(.jsond-entry) {
		margin-bottom: 20px;
	}

	.zen-body :global(.jsond-key) {
		font-size: 13px;
		font-weight: 600;
		color: #374151;
		margin-bottom: 4px;
		letter-spacing: 0.01em;
	}

	.zen-body :global(.depth-1 > .jsond-key) {
		font-size: 16px;
		font-weight: 700;
		color: #111827;
		margin-top: 32px;
		margin-bottom: 8px;
		letter-spacing: -0.2px;
	}

	.zen-body :global(.depth-2 > .jsond-key) {
		font-size: 14px;
		font-weight: 600;
		color: #1F2937;
		margin-top: 20px;
		margin-bottom: 5px;
	}

	.zen-body :global(.jsond-value) {
		font-size: 14.5px;
		line-height: 1.75;
		color: #374151;
	}

	.zen-body :global(.jsond-markdown p) {
		margin: 0 0 10px;
	}

	.zen-body :global(.jsond-markdown code) {
		font-family: 'SF Mono', 'Fira Code', 'Fira Mono', 'Roboto Mono', monospace;
		font-size: 12.5px;
		background: #EDECE8;
		padding: 1px 5px;
		border-radius: 3px;
		color: #1F2937;
	}

	.zen-body :global(.jsond-markdown pre) {
		background: #EDECE8;
		border-radius: 6px;
		padding: 16px;
		overflow-x: auto;
		margin: 12px 0;
	}

	.zen-body :global(.jsond-markdown pre code) {
		background: none;
		padding: 0;
		font-size: 12.5px;
	}

	.zen-body :global(.jsond-table) {
		width: 100%;
		border-collapse: collapse;
		font-size: 13.5px;
		margin: 12px 0;
	}

	.zen-body :global(.jsond-table th),
	.zen-body :global(.jsond-table td) {
		padding: 8px 12px;
		border: 1px solid #D1D5DB;
		text-align: left;
	}

	.zen-body :global(.jsond-table th) {
		background: #EDECE8;
		font-weight: 600;
		color: #1F2937;
	}

	.zen-body :global(.jsond-table tr:nth-child(even) td) {
		background: #F3F1ED;
	}

	/* ---- Responsive ---- */

	@media (max-width: 640px) {
		.zen-page {
			padding: 40px 16px 72px;
		}

		.zen-title {
			font-size: 22px;
		}
	}
</style>
